import humps from 'lodash-humps-ts';
import { stringify } from 'qs';
import { array, boolean, object, number } from 'yup';

import { API_V1_BASE } from '@config';
import { snakes } from '@utils/lodash';
import { authAxios } from '@components/Auth';
import { HealthQuote, HealthQuoteCompare, HealthQuoteList } from '@models/HealthQuote';
import { NumericString, WithParams } from '@utils/typescript';
import { CommissionShareParams, QuoteMemberParams, QuoteOptionParams, quoteOptionParamsSchema } from '@models/Quote';
import { formatFormData } from '@utils/formik';

export type GetAllHealthQuoteParams = QuoteOptionParams & WithParams<{
  ipd?: boolean;
  opd?: boolean;
  health_checkup?: boolean;
  dental?: boolean;
  maternity?: boolean;
  vision?: boolean;
}>;

export const getAllHealthQuoteParamsSchema = quoteOptionParamsSchema.concat(
  object({
    ipd: boolean(),
    opd: boolean(),
    healthCheckup: boolean(),
    dental: boolean(),
    maternity: boolean(),
    vision: boolean(),
  })
);

export type CompareHealthQuoteParams = QuoteOptionParams & WithParams<{
  health_plan_ids: number[];
}>;

export const compareHealthQuoteParamsSchema = quoteOptionParamsSchema.concat(
  object({
    healthPlanIds: array().of(number())
  })
);

export type FindHealthQuoteParams = QuoteOptionParams & WithParams<{
  health_plan_id: number;
}>;

export const findHealthQuoteParamsSchema = quoteOptionParamsSchema.concat(
  object({
    healthPlanId: number(),
  }),
);

export type SendEmailHealthQuoteParams = FindHealthQuoteParams & {
  name: string;
  email: string;
};

export type CreateHealthQuoteParams = FindHealthQuoteParams & CommissionShareParams & WithParams<{
  me: QuoteMemberParams;
  spouse?: QuoteMemberParams;
  sons?: QuoteMemberParams[];
  daughters?: QuoteMemberParams[];
}>;

export type GetHealthQuoteParams = {
  orderNumber: string;
};

export type UpdateHealthQuoteParams = GetHealthQuoteParams & WithParams<{
  me: QuoteMemberParams;
  spouse?: QuoteMemberParams;
  sons?: QuoteMemberParams[];
  daughters?: QuoteMemberParams[];
}>;

type CustomPremium = WithParams<{
  premium: NumericString;
  discount_premium: NumericString;
}>

export type CustomHealthQuoteParams = GetHealthQuoteParams & WithParams<{
  me: CustomPremium;
  spouse?: CustomPremium;
  sons?: CustomPremium[];
  daughters?: CustomPremium[];
  admin_fee_amount: NumericString;
  note: string;
  proof?: File;
}>;

export class HealthQuoteApi {
  static getAll = async ({ me, spouse, sons, daughters, ...rest }: GetAllHealthQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/health_quotes`, {
        params: snakes({ ...rest, members: { me, spouse, sons, daughters } }),
        paramsSerializer: (parameters) => stringify(parameters, { arrayFormat: 'brackets' }),
      })
      .then((response) => humps(response.data) as HealthQuoteList[]);
  };

  static compare = async ({ me, spouse, sons, daughters, ...rest }: CompareHealthQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/health_quotes`, {
        params: snakes({ ...rest, members: { me, spouse, sons, daughters } }),
        paramsSerializer: (parameters) => stringify(parameters, { arrayFormat: 'brackets' }),
      })
      .then((response) => humps(response.data) as HealthQuoteCompare[]);
  };

  static find = async ({ me, spouse, sons, daughters, ...rest }: FindHealthQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/health_quotes/find`, {
        params: snakes({ ...rest, members: { me, spouse, sons, daughters } }),
        paramsSerializer: (parameters) => stringify(parameters, { arrayFormat: 'brackets' }),
      })
      .then((response) => humps(response.data) as HealthQuote);
  };

  static sendEmail = async ({ me, spouse, sons, daughters, ...rest }: SendEmailHealthQuoteParams) => {
    return await authAxios.post(`${API_V1_BASE}/health_quotes/send_email`, snakes({ ...rest, members: { me, spouse, sons, daughters } }));
  };

  static create = async ({ me, spouse, sons, daughters, commissionShares, ...rest }: CreateHealthQuoteParams) => {
    return await authAxios
      .post(`${API_V1_BASE}/user/health_quotes`,
        snakes({ ...rest, members: { me, spouse, sons, daughters }, commissionShares }),
      )
      .then((response) => humps(response.data) as HealthQuote);
  };

  static get = async ({ orderNumber }: GetHealthQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/user/health_quotes/${encodeURIComponent(orderNumber)}`)
      .then((response) => humps(response.data) as HealthQuote);
  };

  static update = async ({ orderNumber, me, spouse, sons, daughters, ...rest }: UpdateHealthQuoteParams) => {
    return await authAxios
      .put(`${API_V1_BASE}/user/health_quotes/${encodeURIComponent(orderNumber)}`,
        snakes({ ...rest, members: { me, spouse, sons, daughters } }),
      )
      .then((response) => humps(response.data) as HealthQuote);
  };

  static custom = async ({ orderNumber, me, spouse, sons, daughters, ...rest }: CustomHealthQuoteParams) => {
    return await authAxios
      .putForm(`${API_V1_BASE}/user/health_quotes/${encodeURIComponent(orderNumber)}/custom`,
        formatFormData(snakes({ ...rest, members: { me, spouse, sons, daughters } })),
      )
      .then((response) => humps(response.data) as HealthQuote);
  };
}
